import React from 'react';
import PropTypes from 'prop-types';
import RemoveButton from '../shared/RemoveButton';
import './GroupPatch.css';

/**
 * GroupPatch — Configurator
 *
 * A set of patches placed together (e.g. a 3-patch pack), selectable as one.
 * Selecting it reveals a black remove button centred on its top-right corner.
 *
 * Figma component set: 4275:23001
 * Variants: State = Default (4275:22998) | Hover (4275:22999) | Selected (4275:23000)
 *
 * Hover is CSS-only; Selected comes from `selected`. The remove button
 * renders only while selected and when `onRemove` is set. `size="large"`
 * uses 52px slots from 1024px up (36px below), matching the large SinglePatch.
 *
 * Patch selection (OVER/LL "Min patchar", Figma 1857:31823): with
 * `onPatchClick` every patch is its own button and `selectedIndex` marks one
 * — white slot + gray-10 stroke on a gray-025 group. The remove button then
 * sits on the GROUP's corner and still removes the whole set: a patch is
 * selectable on its own, never deletable on its own.
 */
const GroupPatch = ({
  patches = [],
  label,
  selected = false,
  selectedIndex = null,
  size = 'default',
  onClick,
  onPatchClick,
  onRemove,
  removeLabel = 'Remove',
  className = '',
}) => {
  const patchSelected = selectedIndex != null && selectedIndex >= 0 && selectedIndex < patches.length;
  const classes = [
    'cfg-group-patch',
    size === 'large' && 'cfg-group-patch--large',
    selected && 'cfg-group-patch--selected',
    patchSelected && 'cfg-group-patch--patch-selected',
    className,
  ].filter(Boolean).join(' ');

  const renderImage = (patch, alt) => (
    <img className="cfg-group-patch__img" src={patch.image} alt={alt} />
  );

  return (
    <div className={classes}>
      {onPatchClick ? (
        <div className="cfg-group-patch__group" role="group" aria-label={label}>
          {patches.map((patch, index) => (
            <button
              key={patch.id ?? index}
              type="button"
              className={[
                'cfg-group-patch__item',
                'cfg-group-patch__item--button',
                index === selectedIndex && 'cfg-group-patch__item--selected',
              ].filter(Boolean).join(' ')}
              onClick={() => onPatchClick(index)}
              aria-pressed={index === selectedIndex}
              aria-label={patch.imageAlt}
            >
              {renderImage(patch, '')}
            </button>
          ))}
        </div>
      ) : (
        <button
          type="button"
          className="cfg-group-patch__group"
          onClick={onClick}
          aria-pressed={selected}
          aria-label={label}
        >
          {patches.map((patch, index) => (
            <span key={patch.id ?? index} className="cfg-group-patch__item">
              {renderImage(patch, label ? '' : patch.imageAlt || '')}
            </span>
          ))}
        </button>
      )}

      {(selected || patchSelected) && onRemove && <RemoveButton onClick={onRemove} label={removeLabel} />}
    </div>
  );
};

GroupPatch.propTypes = {
  /** Patches in the group, left to right */
  patches: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      image: PropTypes.string.isRequired,
      imageAlt: PropTypes.string,
    }),
  ),
  /** Accessible name for the whole group (replaces the per-image alts) */
  label: PropTypes.string,
  /** Selected state — white fill, gray-10 border, remove button */
  selected: PropTypes.bool,
  /** Index of the selected patch (with `onPatchClick`) — white slot, gray-10 stroke, remove button on the group */
  selectedIndex: PropTypes.number,
  /** Slot size — 'default' 36px, 'large' 52px from 1024px up (36px below) */
  size: PropTypes.oneOf(['default', 'large']),
  /** Group click handler (whole-group selection) */
  onClick: PropTypes.func,
  /** Patch click handler — gets the patch index; makes every patch its own button */
  onPatchClick: PropTypes.func,
  /** Remove handler — removes the whole group; the button shows while the group or one of its patches is selected */
  onRemove: PropTypes.func,
  /** Accessible name of the remove button */
  removeLabel: PropTypes.string,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default GroupPatch;
